import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { motion, type Variants } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneOff,
  CalendarCheck,
  Voicemail,
  Globe,
  Bot,
  Headphones,
  Shield,
  CheckCircle,
  Lock,
  Eye,
  EyeOff,
  User,
  Check,
  ChevronsUpDown,
  QrCode,
  Download,
  ExternalLink,
  Copy,
  Share2,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import {
  AUTH_USER_COOKIE,
  getAuthJsonCookie,
} from '@/utils/authCookies';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import languagesData from '@/config/languages.json';
import dialectIndexRaw from '@/config/dialect_index.json';
import {
  PipecatClient,
  type PipecatClientOptions,
  type TransportState,
  type TranscriptData,
  type BotOutputData,
  type Participant,
} from '@pipecat-ai/client-js';
import { WebSocketTransport } from '@pipecat-ai/websocket-transport';
import { useSEO } from '@/hooks/useSEO';
import { PublicLayout } from '../layout/PublicLayout';
import {
  CUSTOM_BOT_KEY,
  getBotByKey,
  getPublicBots,
  getBotImageUrl,
  useVoiceBots,
  type VoiceBot,
} from '@/config/voiceBots';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const VOICE_API_URL = import.meta.env.VITE_VOICE_API_URL || API_URL;

// Input and output rates are DECOUPLED (matching the pipecat freeze-test ref and
// the server's /ws/pipecat pipeline):
//   - RECORDER 16 kHz: the server runs Silero VAD on the Gemini user aggregator,
//     and Silero only accepts 8000/16000. Mic must arrive VAD-legal, so the
//     server sets audio_in_sample_rate=16000 — the recorder must match it.
//   - PLAYER 24 kHz: Gemini Live's native rate, passed straight through (no
//     resample) and played 1:1. Server audio_out_sample_rate=24000 must match.
// See onConnected for the iOS Safari player-rate guard.
const RECORDER_SAMPLE_RATE = 16000;
const PLAYER_SAMPLE_RATE = 24000;

const DEMO_PASSWORD_STORAGE_KEY = 'raisy_demo_password_v1';

// D-006: the firebase user snapshot lives in a cookie, not localStorage.
interface StoredFirebaseUserSnapshot {
  photoURL?: string | null;
}

function readFirebaseUserSnapshot(): StoredFirebaseUserSnapshot | null {
  try {
    if (typeof window === 'undefined') return null;
    return getAuthJsonCookie<StoredFirebaseUserSnapshot>(AUTH_USER_COOKIE);
  } catch {
    return null;
  }
}

function isRaisyAuthenticated(): boolean {
  try {
    return typeof window !== 'undefined' && readFirebaseUserSnapshot() !== null;
  } catch {
    return false;
  }
}

const USER_PHOTO_CACHE_KEY = 'raisy_user_photo_dataurl';

function getUserPhotoUrl(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    const cached = window.localStorage.getItem(USER_PHOTO_CACHE_KEY);
    if (cached) return cached;
    return readFirebaseUserSnapshot()?.photoURL || null;
  } catch {
    return null;
  }
}

function cacheUserPhoto() {
  try {
    if (typeof window === 'undefined') return;
    const url = readFirebaseUserSnapshot()?.photoURL;
    if (!url) return;
    if (window.localStorage.getItem(USER_PHOTO_CACHE_KEY)) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 128, 128);
        const dataUrl = canvas.toDataURL('image/webp', 0.85);
        window.localStorage.setItem(USER_PHOTO_CACHE_KEY, dataUrl);
      } catch {
        // Canvas taint or other issue — ignore.
      }
    };
    img.src = url;
  } catch {
    // ignore
  }
}

function getSavedDemoPassword(): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(DEMO_PASSWORD_STORAGE_KEY) : null;
  } catch {
    return null;
  }
}

function saveDemoPassword(password: string) {
  try {
    window.localStorage.setItem(DEMO_PASSWORD_STORAGE_KEY, password);
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

function clearSavedDemoPassword() {
  try {
    window.localStorage.removeItem(DEMO_PASSWORD_STORAGE_KEY);
  } catch {
    // ignore
  }
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

const capabilities = [
  {
    icon: <PhoneOutgoing className="w-6 h-6" />,
    title: 'Outbound AI Calls',
    description:
      'Your AI agent dials leads, introduces itself, handles objections and books meetings — at any scale, any timezone.',
  },
  {
    icon: <PhoneIncoming className="w-6 h-6" />,
    title: 'Inbound Reception',
    description:
      'Answer incoming calls 24/7, route to the right department, capture intent and never miss a hot lead.',
  },
  {
    icon: <CalendarCheck className="w-6 h-6" />,
    title: 'Appointment Booking',
    description:
      'Qualify interest and schedule qualified calls directly into human calendars. No back-and-forth emails.',
  },
  {
    icon: <Voicemail className="w-6 h-6" />,
    title: 'Smart Voicemail',
    description:
      "When prospects don't pick up, leave a concise, personalized voicemail and trigger automatic follow-up.",
  },
  {
    icon: <Globe className="w-6 h-6" />,
    title: 'Multilingual by Default',
    description:
      'Speak to prospects in their language — German, English, Polish and more — with accent and tone matched to the brand.',
  },
  {
    icon: <Headphones className="w-6 h-6" />,
    title: 'Human Handoff',
    description:
      'AI handles the repetitive 80%. When trust or complexity matters, transfer to a human with full context.',
  },
];

const useCases = [
  {
    title: '24/7 Care & Pflege-Vermittlung',
    desc: 'Qualify families, explain care options and book assessment calls for live-in care agencies.',
  },
  {
    title: 'B2B Lead Generation',
    desc: 'Cold-call target accounts, identify decision-makers and book demos for sales teams.',
  },
  {
    title: 'Inbound Customer Service',
    desc: 'Answer product questions, route support tickets and capture return/exchange intents.',
  },
  {
    title: 'Recruiting & Talent Outreach',
    desc: 'Reach passive candidates, pre-screen interest and schedule recruiter interviews.',
  },
];

// Pre-call options the user can pick in the popup. All map 1:1 to /ws/pipecat
// query params that the backend reads (see main.py websocket handler):
//   botName  -> p2_caller_name  (the BOT's OWN name — the voice the caller hears;
//                                P2, not the caller. Empty = the brand/kernel name.)
//   language -> p2_language     (the OPENING language; the kernel still auto-adapts
//                                if the caller switches — a standardized override
//                                that applies to every bot the same way)
//   voice    -> p2_gemini_voice (Gemini Live voice/timbre; gender is auto-matched
//                                to the voice downstream)
// Empty values are simply omitted from the URL so the bot's own defaults apply.
// Curated subset of the Gemini Live voices in raisy_com/src/language_index.json.
// `gender` is informational for the label; the backend derives the bot's spoken
// name from the voice gender automatically.
const VOICE_OPTIONS = [
  { id: '', label: 'Default (agent’s own voice)' },
  { id: 'Aoede', label: 'Aoede — female, gentle' },
  { id: 'Kore', label: 'Kore — female, authoritative' },
  { id: 'Schedar', label: 'Schedar — male, warm' },
  { id: 'Puck', label: 'Puck — male, energetic' },
  { id: 'Charon', label: 'Charon — male, calm' },
] as const;

export interface PrecallOptions {
  botName: string; // '' = use the bot's brand/kernel name
  language: string; // '' = use the bot's own default opening language (2-letter code)
  language_accent: string; // '' = use the bot's default accent/dialect
  voice: string; // '' = use the bot's default voice
}

interface DialectOption {
  value: string;
  label: string;
}

const DIALECT_INDEX: Record<string, DialectOption[]> = dialectIndexRaw as Record<string, DialectOption[]>;

const accentOptionsForLanguage = (languageCode: string): DialectOption[] =>
  DIALECT_INDEX[languageCode] ?? [];

const isValidAccentForLanguage = (languageCode: string, accent: string): boolean => {
  if (!accent || !languageCode) return false;
  const opts = accentOptionsForLanguage(languageCode);
  if (opts.length === 0) return true; // no presets → free text is allowed
  return opts.some((o) => o.value === accent);
};

// Full supported-language list (97), generated from raisy_com/src/language_index.json.
interface LanguageEntry {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}
const ALL_LANGUAGES = languagesData as LanguageEntry[];
// Pinned to the top of the picker, above a divider, for quick access.
const PINNED_LANGUAGE_CODES = ['en', 'de', 'zh'];
const PINNED_LANGUAGES = PINNED_LANGUAGE_CODES
  .map((c) => ALL_LANGUAGES.find((l) => l.code === c))
  .filter((l): l is LanguageEntry => Boolean(l));
const OTHER_LANGUAGES = ALL_LANGUAGES.filter((l) => !PINNED_LANGUAGE_CODES.includes(l.code));

// Searchable language combobox: "Agent default" + pinned (EN/DE/ZH) above a
// divider, then all remaining languages, filtered by a search box.
const LanguagePicker = ({
  value,
  onChange,
}: {
  value: string; // '' = agent default
  onChange: (code: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const selected = value ? ALL_LANGUAGES.find((l) => l.code === value) : undefined;

  const renderItem = (l: LanguageEntry) => (
    <CommandItem
      key={l.code}
      // value drives cmdk's fuzzy search — include name + native + code.
      value={`${l.name} ${l.nativeName} ${l.code}`}
      onSelect={() => {
        onChange(l.code);
        setOpen(false);
      }}
    >
      <span className="mr-2">{l.flag}</span>
      <span className="truncate">{l.name}</span>
      {l.nativeName && l.nativeName !== l.name && (
        <span className="ml-1.5 text-xs text-slate-400 truncate">· {l.nativeName}</span>
      )}
      <Check className={`ml-auto h-4 w-4 ${value === l.code ? 'opacity-100' : 'opacity-0'}`} />
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate">
            {selected ? (open ? `${selected.flag} ${selected.name}` : selected.flag) : 'Agent default'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search language…" />
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="agent default"
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                <span className="mr-2">🤖</span>
                <span>Agent default</span>
                <Check className={`ml-auto h-4 w-4 ${value === '' ? 'opacity-100' : 'opacity-0'}`} />
              </CommandItem>
              {PINNED_LANGUAGES.map(renderItem)}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>{OTHER_LANGUAGES.map(renderItem)}</CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Searchable accent/dialect combobox keyed off the selected base language.
// When the language has no presets we fall back to a plain text input so a
// custom instruction can still be entered.
const AccentPicker = ({
  languageCode,
  value,
  onChange,
}: {
  languageCode: string; // '' = agent default / no language chosen
  value: string;
  onChange: (accent: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const options = accentOptionsForLanguage(languageCode);
  const selected = value ? options.find((o) => o.value === value) : undefined;

  // No presets → free text. We keep the value trimmed and still send it as
  // p2_language_accent when non-empty.
  if (options.length === 0) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={languageCode ? 'Custom accent…' : 'Select a language first'}
        disabled={!languageCode}
        className="dark:bg-slate-900 dark:border-slate-700 dark:text-white h-9"
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-sm text-foreground hover:bg-accent hover:text-accent-foreground"
        >
          <span className="truncate">{selected ? selected.label : 'Default accent'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accent…" />
          <CommandList>
            <CommandEmpty>No accent found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="default accent"
                onSelect={() => {
                  onChange('');
                  setOpen(false);
                }}
              >
                <span>Default accent</span>
                <Check className={`ml-auto h-4 w-4 ${value === '' ? 'opacity-100' : 'opacity-0'}`} />
              </CommandItem>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={`${o.label} ${o.value}`}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <span className="truncate">{o.label}</span>
                  <Check className={`ml-auto h-4 w-4 ${value === o.value ? 'opacity-100' : 'opacity-0'}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const TelephonyPage = () => {
  useSEO({
    title: 'Raisy AI Telephony — Voice Agents That Call, Listen & Book',
    description:
      'Outbound and inbound AI phone agents for lead generation, appointment booking and customer service. Hire a voice workforce that works 24/7 in any language.',
    keywords:
      'AI phone agent, voice AI, outbound calling, inbound reception, appointment booking, AI telephony, lead generation, voicebot',
    ogTitle: 'Raisy AI Telephony — Voice Agents That Call, Listen & Book',
    ogDescription:
      'Outbound and inbound AI phone agents for lead generation, appointment booking and customer service.',
    ogImage: 'https://raisy.team/og-telephony.jpg',
    ogType: 'website',
    canonicalUrl: 'https://raisy.team/telephony',
  });

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    role: '',
    use_case: '',
    expected_volume: '',
    timeline: '',
    bot_interest: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const { bots, loading: botsLoading, error: botsError } = useVoiceBots();
  const publicBots = useMemo(() => getPublicBots(bots), [bots]);
  const heroLanguages = useMemo(() => {
    const fromBots = Array.from(new Set(publicBots.map((b) => b.language)));
    return fromBots.length
      ? fromBots
      : ['German', 'English', 'Polish'];
  }, [publicBots]);

  const [selectedBotKey, setSelectedBotKey] = useState<string>('');
  const [callModalOpen, setCallModalOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const selectedBot = useMemo(
    () => getBotByKey(bots, selectedBotKey),
    [bots, selectedBotKey],
  );

  const [searchParams, setSearchParams] = useSearchParams();

  // Open the popup from a direct link (?bot=...) immediately from the bundled bots (no backend wait).
  const hasAutoOpenedRef = useRef(false);
  useEffect(() => {
    if (botsLoading || publicBots.length === 0) return;
    if (hasAutoOpenedRef.current) return;
    const key = searchParams.get('bot');
    if (!key || !publicBots.some((b) => b.key === key)) return;
    const bot = getBotByKey(bots, key);
    if (!bot) return;
    hasAutoOpenedRef.current = true;
    setSelectedBotKey(key);
    setPrecall({
      botName: bot.persona_name || '',
      language: botLanguageCode(bot.language),
      language_accent: bot.language_accent || '',
      voice: bot.default_voice || '',
    });
    setCallModalOpen(true);
  }, [botsLoading, publicBots, bots, searchParams]);

  // Predictive warm-up: a deep link (?bot=) is strong call intent, so kick the
  // voice backend's cold start NOW (parallel with the password step) rather than
  // at Call time. Cheap: GET /health only boots the container + lifespan — it does
  // NOT start a Gemini session. JS-gated (useEffect) so crawlers/unfurlers/curl
  // never trigger it; fires once per visit. no-store ensures it reaches origin.
  const warmupFiredRef = useRef(false);
  useEffect(() => {
    if (warmupFiredRef.current) return;
    if (!searchParams.get('bot')) return;
    warmupFiredRef.current = true;
    fetch(`${VOICE_API_URL}/health`, { cache: 'no-store' }).catch(() => {});
  }, [searchParams]);

  // Keep the URL in sync with the open popup (never the password).
  // Only remove ?bot when the user actually closes the modal, not on initial load.
  const prevCallModalOpenRef = useRef(callModalOpen);
  useEffect(() => {
    prevCallModalOpenRef.current = callModalOpen;
  });
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (callModalOpen && selectedBotKey) {
      if (next.get('bot') !== selectedBotKey) {
        next.set('bot', selectedBotKey);
        setSearchParams(next, { replace: true });
      }
    } else if (!callModalOpen && prevCallModalOpenRef.current && next.has('bot')) {
      next.delete('bot');
      setSearchParams(next, { replace: true });
    }
  }, [callModalOpen, selectedBotKey, searchParams, setSearchParams]);

  // Optional pre-call customization chosen in the popup. Defaults keep the
  // one-tap flow: empty name/voice and empty language fall back to the bot's own.
  const [precall, setPrecall] = useState<PrecallOptions>({
    botName: '',
    language: '',
    language_accent: '',
    voice: '',
  });
  const updatePrecall = (patch: Partial<PrecallOptions>) =>
    setPrecall((prev) => ({ ...prev, ...patch }));

  const [demoPassword, setDemoPassword] = useState('');
  const qrWrapperRef = useRef<HTMLDivElement>(null);

  // Full shareable URL for the currently open bot modal (password is never in the URL).
  const callUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    try {
      const url = new URL(window.location.href);
      url.hash = '';
      return url.toString();
    } catch {
      return '';
    }
  }, [searchParams]);
  const [showDemoPassword, setShowDemoPassword] = useState(false);
  const [qrCopied, setQrCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [demoAuthStatus, setDemoAuthStatus] = useState<'idle' | 'checking' | 'granted' | 'denied'>('idle');
  const autoVerifiedRef = useRef<string | null>(null);

  const verifyDemoPassword = useCallback(
    async (password: string) => {
      if (!selectedBot) return;
      setDemoAuthStatus('checking');
      try {
        const res = await fetch(`${VOICE_API_URL}/api/v1/telephony/demo-auth`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
        if (res.ok) {
          setDemoAuthStatus('granted');
          saveDemoPassword(password);
        } else {
          setDemoAuthStatus('denied');
          clearSavedDemoPassword();
        }
      } catch {
        setDemoAuthStatus('denied');
        clearSavedDemoPassword();
      }
    },
    [selectedBot],
  );

  const checkDemoPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyDemoPassword(demoPassword);
  };

  // Authenticated Raisy users bypass the demo password gate entirely.
  useEffect(() => {
    if (isRaisyAuthenticated()) {
      setDemoAuthStatus('granted');
    }
  }, []);

  // Returning users: restore saved password and auto-verify once per bot selection.
  useEffect(() => {
    if (!selectedBot) return;
    if (isRaisyAuthenticated()) {
      setDemoAuthStatus('granted');
      return;
    }
    if (autoVerifiedRef.current === selectedBot.key) return;
    const saved = getSavedDemoPassword();
    if (saved) {
      setDemoPassword(saved);
      autoVerifiedRef.current = selectedBot.key;
      verifyDemoPassword(saved);
    }
  }, [selectedBot, verifyDemoPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch(`${API_URL}/api/v1/telephony/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, source: 'raisy.team/telephony' }),
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        setFormStatus('success');
        setForm({
          name: '',
          email: '',
          company: '',
          phone: '',
          role: '',
          use_case: '',
          expected_volume: '',
          timeline: '',
          bot_interest: '',
        });
      } else {
        setFormStatus('error');
        setFormError(data.detail || data.error || 'Something went wrong');
      }
    } catch (err) {
      setFormStatus('error');
      setFormError(err instanceof Error ? err.message : 'Network error');
    }
  };

  const updateField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isFormValid =
    form.name.trim() && form.email.trim() && form.use_case.trim();

  return (
    <PublicLayout>
      {/* Hero */}
      <motion.section
        className="relative overflow-hidden bg-slate-50 dark:bg-slate-950"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(6,182,212,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(6,182,212,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative">
          <motion.div className="text-center space-y-6 md:space-y-8">
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/20 dark:text-cyan-400 dark:border-cyan-800 rounded-full text-sm font-medium border"
              variants={itemVariants}
            >
              <span className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
              New: AI Voice Agents
            </motion.div>

            <motion.h1
              className="text-display font-bold text-slate-900 dark:text-white leading-tight max-w-5xl mx-auto"
              variants={itemVariants}
            >
              AI Agents That{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                Call, Listen & Book
              </span>
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto leading-relaxed"
              variants={itemVariants}
            >
              Outbound and inbound phone calls handled by specialized AI agents. Scale your
              sales, support and recruiting without hiring another call center.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
              variants={itemVariants}
            >
              <a href="#try-call">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 gap-2">
                  Try a Live Call <Phone className="w-4 h-4" />
                </Button>
              </a>
              <a href="#capabilities">
                <Button size="lg" variant="outline" className="px-8 dark:text-white dark:border-slate-600 dark:hover:bg-slate-800">
                  See Capabilities
                </Button>
              </a>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-12 md:pt-20 border-t border-slate-200 dark:border-slate-800"
              variants={itemVariants}
            >
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">24/7</div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Always-on voice agents</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">&lt;500ms</div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Response latency target</p>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white">Multilingual</div>
                <div className="flex flex-wrap items-center justify-center gap-1.5">
                  {heroLanguages.map((lang) => (
                    <CountryFlag key={lang} language={lang} className="h-4 w-6 rounded-sm shadow" />
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Live call demo — top */}
      <section id="try-call" className="bg-white dark:bg-slate-900 py-20 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
              Live Demo
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Call an AI Agent
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              Choose an agent below to open a live browser conversation. Unlock the call with a demo password.
            </p>
          </div>

          <BotSelector
            bots={publicBots}
            loading={botsLoading}
            selectedBotKey={selectedBotKey}
            onSelect={(key) => {
              setSelectedBotKey(key);
              const bot = getBotByKey(bots, key);
              setPrecall({
                botName: bot?.persona_name || '',
                language: bot ? botLanguageCode(bot.language) : '',
                language_accent: bot?.language_accent || '',
                voice: bot?.default_voice || '',
              });
              setCallModalOpen(true);
            }}
          />

          {/* Call / chat popup — opens for the chosen agent so the bot and its
              live call stay visually attached instead of detaching down-page. */}
          <Dialog open={callModalOpen} onOpenChange={setCallModalOpen}>
            <DialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 sm:max-w-xl p-0 overflow-hidden max-h-[90vh]">
              {selectedBot && (
                <div className="p-4 sm:p-6 md:p-8 max-h-[90vh] overflow-y-auto">
                  <DialogTitle className="sr-only">
                    Call {selectedBot.persona_name || selectedBot.name}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Start a live voice call with {selectedBot.persona_name || selectedBot.name}, the{' '}
                    {selectedBot.role || selectedBot.category}.
                  </DialogDescription>
                  {/* Chosen-agent header — keeps the picked bot's identity in view */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600 to-teal-700 shadow-lg">
                      <img
                        src={getBotImageUrl(selectedBot.key, 128)}
                        alt={selectedBot.persona_name || selectedBot.name}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white leading-tight flex items-center gap-2">
                        <CountryFlag language={selectedBot.language} accent={selectedBot.language_accent} className="h-5 w-7" />
                        {selectedBot.persona_name || selectedBot.name}
                      </h3>
                      <p className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                        {selectedBot.role || selectedBot.category}
                      </p>
                      {selectedBot.entity_name && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{selectedBot.entity_name}</p>
                      )}
                      <p className="text-label-2xs uppercase tracking-wider text-slate-500 mt-0.5">AI voice agent</p>
                    </div>
                  </div>

                  {/* Optional pre-call customization — collapsed on mobile so the
                      Call button is visible without scrolling. */}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setSettingsOpen((o) => !o)}
                    className="mb-3 w-full md:hidden"
                  >
                    {settingsOpen ? 'Hide settings' : 'Show settings (optional)'}
                  </Button>
                  <div
                    className={`mb-6 grid grid-cols-1 sm:grid-cols-2 gap-3 ${settingsOpen ? '' : 'hidden'} md:grid`}
                  >
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400">Bot’s name</Label>
                      <Input
                        value={precall.botName}
                        onChange={(e) => updatePrecall({ botName: e.target.value })}
                        placeholder="Optional"
                        className="dark:bg-slate-900 dark:border-slate-700 dark:text-white h-9"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400">Voice</Label>
                      <Select
                        value={precall.voice || 'default'}
                        onValueChange={(v) => updatePrecall({ voice: v === 'default' ? '' : v })}
                      >
                        <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white h-9">
                          <SelectValue placeholder="Default" />
                        </SelectTrigger>
                        <SelectContent>
                          {VOICE_OPTIONS.map((v) => (
                            <SelectItem key={v.id || 'default'} value={v.id || 'default'}>
                              {v.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400">Language</Label>
                      <LanguagePicker
                        value={precall.language}
                        onChange={(code) => {
                          // If the new base language does not support the current
                          // accent override, reset the accent so we never send an
                          // invalid language + accent pair.
                          const nextAccent = isValidAccentForLanguage(code, precall.language_accent)
                            ? precall.language_accent
                            : '';
                          updatePrecall({ language: code, language_accent: nextAccent });
                        }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-400">Accent / Dialect</Label>
                      <AccentPicker
                        languageCode={precall.language}
                        value={precall.language_accent}
                        onChange={(accent) => updatePrecall({ language_accent: accent })}
                      />
                    </div>
                  </div>

                  {demoAuthStatus === 'granted' ? (
                    <CallWidget bot={selectedBot} voiceApiUrl={VOICE_API_URL} precall={precall} />
                  ) : (
                    <form onSubmit={checkDemoPassword} className="space-y-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center">
                          <Lock className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-slate-900 dark:text-white">Have a Demo Password?</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            Enter it to start a live call with {selectedBot.persona_name || selectedBot.name}.
                          </p>
                        </div>
                      </div>

                      <div className="relative">
                        <Input
                          type={showDemoPassword ? 'text' : 'password'}
                          value={demoPassword}
                          onChange={(e) => setDemoPassword(e.target.value)}
                          placeholder="Enter demo password"
                          autoFocus
                          className="dark:bg-slate-900 dark:border-slate-700 dark:text-white pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowDemoPassword((s) => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900 dark:hover:text-white"
                        >
                          {showDemoPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>

                      {demoAuthStatus === 'denied' && (
                        <div className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
                          Invalid password or demo not enabled.
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={demoAuthStatus === 'checking' || !demoPassword.trim()}
                        className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                        size="lg"
                      >
                        {demoAuthStatus === 'checking' ? 'Checking...' : `Call ${selectedBot.persona_name || selectedBot.name}`}
                      </Button>

                      <p className="text-xs text-slate-400 text-center">
                        No password? Request access below and tell us which agent interests you.
                      </p>
                    </form>
                  )}

                  {/* Shareable QR code and link for the current bot URL */}
                  {callUrl && (
                    <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <QrCode className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Share this agent</h4>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-4">
                        <div
                          ref={qrWrapperRef}
                          className="shrink-0 rounded-lg overflow-hidden bg-white p-1"
                        >
                          <QRCodeCanvas
                            value={callUrl}
                            size={128}
                            bgColor="#ffffff"
                            fgColor="#0f172a"
                            level="M"
                            includeMargin={false}
                          />
                        </div>
                        <div className="flex-1 min-w-0 space-y-3">
                          <p className="text-xs text-slate-400 break-all leading-relaxed">
                            {callUrl}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const canvas = qrWrapperRef.current?.querySelector('canvas');
                                if (!canvas) return;
                                const dataUrl = (canvas as HTMLCanvasElement).toDataURL('image/png');
                                if (navigator.clipboard && typeof navigator.clipboard.write === 'function') {
                                  fetch(dataUrl)
                                    .then((res) => res.blob())
                                    .then((blob) =>
                                      navigator.clipboard.write([
                                        new ClipboardItem({ 'image/png': blob }),
                                      ]),
                                    )
                                    .then(() => {
                                      setQrCopied(true);
                                      setTimeout(() => setQrCopied(false), 2000);
                                    })
                                    .catch(() => {
                                      const link = document.createElement('a');
                                      link.href = dataUrl;
                                      link.download = `raisy-${selectedBot.key}-qr.png`;
                                      link.click();
                                    });
                                } else {
                                  const link = document.createElement('a');
                                  link.href = dataUrl;
                                  link.download = `raisy-${selectedBot.key}-qr.png`;
                                  link.click();
                                }
                              }}
                              
                            >
                              <Copy className="w-4 h-4 mr-1" /> {qrCopied ? 'Copied!' : 'Copy QR'}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const canvas = qrWrapperRef.current?.querySelector('canvas');
                                if (!canvas) return;
                                const link = document.createElement('a');
                                link.href = (canvas as HTMLCanvasElement).toDataURL('image/png');
                                link.download = `raisy-${selectedBot.key}-qr.png`;
                                link.click();
                              }}
                              
                            >
                              <Download className="w-4 h-4 mr-1" /> Download
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const canvas = qrWrapperRef.current?.querySelector('canvas');
                                if (!canvas) return;
                                const dataUrl = (canvas as HTMLCanvasElement).toDataURL('image/png');
                                const win = window.open('', '_blank');
                                if (win) {
                                  win.document.write(
                                    `<html><head><title>QR code for ${selectedBot.persona_name || selectedBot.name}</title></head><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100vh;background:#0f172a"><img src="${dataUrl}" style="max-width:90vw;max-height:90vh;border-radius:12px" /></body></html>`
                                  );
                                  win.document.close();
                                }
                              }}
                              
                            >
                              <ExternalLink className="w-4 h-4 mr-1" /> Open
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (navigator.share) {
                                  navigator
                                    .share({
                                      title: `Call ${selectedBot.persona_name || selectedBot.name} on Raisy`,
                                      url: callUrl,
                                    })
                                    .catch(() => {
                                      // User cancelled share — no-op.
                                    });
                                } else {
                                  navigator.clipboard.writeText(callUrl).then(() => {
                                    setLinkCopied(true);
                                    setTimeout(() => setLinkCopied(false), 2000);
                                  });
                                }
                              }}
                              
                            >
                              <Share2 className="w-4 h-4 mr-1" /> Share link
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(callUrl).then(() => {
                                  setLinkCopied(true);
                                  setTimeout(() => setLinkCopied(false), 2000);
                                });
                              }}
                              
                            >
                              <Copy className="w-4 h-4 mr-1" /> {linkCopied ? 'Copied!' : 'Copy link'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {/* Capabilities */}
      <section id="capabilities" className="bg-slate-50 dark:bg-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              What the Voice Workforce Does
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-2xl mx-auto">
              One platform for every phone-touchpoint. Inbound, outbound, follow-up and handoff.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, idx) => (
              <motion.div
                key={idx}
                className="p-6 md:p-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-100 to-teal-100 dark:from-cyan-900 dark:to-teal-900 flex items-center justify-center text-cyan-600 dark:text-cyan-400 mb-4">
                  {cap.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">{cap.title}</h3>
                <p className="text-slate-600 dark:text-slate-300">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call Flow Visual */}
      <section className="bg-slate-50 dark:bg-slate-900 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">How a Call Works</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300">From first ring to booked outcome — fully autonomous, fully auditable.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { step: '01', title: 'Dial', desc: 'AI places or answers the call' },
              { step: '02', title: 'Listen', desc: 'Real-time STT + intent understanding' },
              { step: '03', title: 'Respond', desc: 'Brand-trained LLM replies naturally' },
              { step: '04', title: 'Act', desc: 'Book, qualify, escalate or voicemail' },
              { step: '05', title: 'Learn', desc: 'Transcripts feed the improvement loop' },
            ].map((item, idx) => (
              <div
                key={idx}
                className="relative p-6 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center"
              >
                <div className="text-2xl font-bold text-cyan-500 mb-2">{item.step}</div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">{item.title}</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">{item.desc}</p>
                {idx < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-[2px] bg-cyan-300 dark:bg-cyan-800" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="bg-slate-50 dark:bg-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">Built For These Use Cases</h2>
            <p className="text-lg text-slate-600 dark:text-slate-300">Start with one, expand to every phone-touchpoint.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {useCases.map((uc, idx) => (
              <div
                key={idx}
                className="flex items-start gap-4 p-6 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700"
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-100 dark:bg-cyan-900/50 flex items-center justify-center text-cyan-600 dark:text-cyan-400 flex-shrink-0">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{uc.title}</h3>
                  <p className="text-slate-600 dark:text-slate-300">{uc.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust badges */}
      <section className="bg-slate-50 dark:bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-cyan-500 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Compliance First</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">AI disclosure, recording notice and DNC handling built in.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <CheckCircle className="w-8 h-8 text-teal-500 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Confidence-Based Autonomy</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">Start with human review. Earn full autonomy as the agent proves itself.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-8 h-8 text-emerald-500 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-slate-900 dark:text-white mb-1">Bring Your Own Number</h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">Connect existing landline or mobile numbers via SIP or call forwarding.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Access Form */}
      <section id="request-access" className="bg-slate-50 dark:bg-slate-950 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
              Request Early Access
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Tell Us What You Want to Call About
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-300">
              We review every request manually. The more detail you share, the faster we can design the right pilot.
            </p>
          </div>

          <div className="bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 md:p-10">
            {formStatus === 'success' ? (
              <div className="text-center space-y-4 py-8">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Request Received</h3>
                <p className="text-slate-600 dark:text-slate-300">
                  We&apos;ll review your use case and get back to you within 2 business days.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setFormStatus('idle')}
                  className="dark:text-white dark:border-slate-600"
                >
                  Submit another request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-slate-900 dark:text-white">
                      Name <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      placeholder="Max Mustermann"
                      required
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-900 dark:text-white">
                      Email <span className="text-red-400">*</span>
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField('email', e.target.value)}
                      placeholder="max@company.com"
                      required
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-slate-900 dark:text-white">Company</Label>
                    <Input
                      id="company"
                      value={form.company}
                      onChange={(e) => updateField('company', e.target.value)}
                      placeholder="Acme GmbH"
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-900 dark:text-white">Phone</Label>
                    <Input
                      id="phone"
                      value={form.phone}
                      onChange={(e) => updateField('phone', e.target.value)}
                      placeholder="+49 170 1234567"
                      className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="role" className="text-slate-900 dark:text-white">Your Role</Label>
                    <Select value={form.role} onValueChange={(v) => updateField('role', v)}>
                      <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="founder">Founder / CEO</SelectItem>
                        <SelectItem value="sales">Sales / BD Lead</SelectItem>
                        <SelectItem value="marketing">Marketing Lead</SelectItem>
                        <SelectItem value="operations">Operations / COO</SelectItem>
                        <SelectItem value="investor">Investor</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expected_volume" className="text-slate-900 dark:text-white">Expected Monthly Call Volume</Label>
                    <Select
                      value={form.expected_volume}
                      onValueChange={(v) => updateField('expected_volume', v)}
                    >
                      <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                        <SelectValue placeholder="Select volume" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="< 1k">&lt; 1,000 calls / month</SelectItem>
                        <SelectItem value="1k-10k">1,000 – 10,000 calls / month</SelectItem>
                        <SelectItem value="10k-50k">10,000 – 50,000 calls / month</SelectItem>
                        <SelectItem value="> 50k">&gt; 50,000 calls / month</SelectItem>
                        <SelectItem value="unsure">Not sure yet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bot_interest" className="text-slate-900 dark:text-white">
                    Which AI agent interests you? <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={form.bot_interest}
                    onValueChange={(v) => updateField('bot_interest', v)}
                    required
                  >
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                      <SelectValue placeholder="Select an agent or request a custom one" />
                    </SelectTrigger>
                    <SelectContent>
                      {botsLoading ? (
                        <SelectItem value="loading" disabled>Loading agents…</SelectItem>
                      ) : (
                        <>
                          {publicBots.map((bot) => (
                            <SelectItem key={bot.key} value={bot.key}>
                              {bot.flag} {bot.persona_name || bot.name}
                            </SelectItem>
                          ))}
                          <SelectItem value={CUSTOM_BOT_KEY}>Custom agent for my use case</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="timeline" className="text-slate-900 dark:text-white">Timeline</Label>
                  <Select value={form.timeline} onValueChange={(v) => updateField('timeline', v)}>
                    <SelectTrigger className="dark:bg-slate-900 dark:border-slate-700 dark:text-white">
                      <SelectValue placeholder="When do you want to start?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asap">ASAP</SelectItem>
                      <SelectItem value="1-3-months">1–3 months</SelectItem>
                      <SelectItem value="3-6-months">3–6 months</SelectItem>
                      <SelectItem value="exploring">Just exploring</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="use_case" className="text-slate-900 dark:text-white">
                    What would you use AI telephony for? <span className="text-red-400">*</span>
                  </Label>
                  <Textarea
                    id="use_case"
                    value={form.use_case}
                    onChange={(e) => updateField('use_case', e.target.value)}
                    placeholder="e.g. We run a 24/7 care agency and need outbound qualification calls to families requesting information on our website."
                    required
                    rows={5}
                    className="dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                  />
                </div>

                {formStatus === 'error' && (
                  <div className="text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg p-3">
                    {formError || 'Something went wrong. Please try again.'}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={!isFormValid || formStatus === 'submitting'}
                  className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                  size="lg"
                >
                  {formStatus === 'submitting' ? 'Submitting...' : 'Request Access'}
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  No spam. We use this information only to prepare a relevant pilot proposal.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

function botLanguageCode(language: string): string {
  switch (language.toLowerCase()) {
    case 'german':
      return 'de';
    case 'english':
      return 'en';
    case 'polish':
      return 'pl';
    default:
      return 'en';
  }
}

function countryCodeForLanguage(language: string, accent?: string): string | null {
  const accentLower = accent?.toLowerCase() ?? '';
  if (accentLower.includes('indian')) return 'in';
  if (accentLower.includes('american')) return 'us';
  if (accentLower.includes('british') || accentLower.includes('scottish') || accentLower.includes('irish')) return 'gb';
  switch (language.toLowerCase()) {
    case 'english':
      return 'gb';
    case 'german':
      return 'de';
    case 'polish':
      return 'pl';
    default:
      return null;
  }
}

const CountryFlag = ({
  language,
  accent,
  className = '',
}: {
  language: string;
  accent?: string;
  className?: string;
}) => {
  const code = countryCodeForLanguage(language, accent);
  const label = accent ? `${language} (${accent})` : language;
  if (!code) {
    return <span className={className}>{label}</span>;
  }
  return (
    <img
      src={`https://flagcdn.com/w40/${code}.png`}
      alt={label}
      title={label}
      loading="lazy"
      className={`inline-block object-cover rounded-sm shadow ${className}`}
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

interface BotDemoCardProps {
  bot: VoiceBot;
  onSelect: () => void;
}

const BotDemoCard = ({ bot, onSelect }: BotDemoCardProps) => {
  const [imgError, setImgError] = useState(false);
  const displayName = bot.persona_name || bot.name;

  return (
    <div
      className="group relative aspect-square overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:border-cyan-500 hover:shadow-lg hover:shadow-cyan-950/30 transition-all cursor-pointer"
      onClick={onSelect}
    >
      {/* Hero image */}
      {!imgError ? (
        <img
          src={getBotImageUrl(bot.key, 512)}
          alt={displayName}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={() => setImgError(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-cyan-600 to-teal-700">
          <Bot className="h-20 w-20 text-white/30" />
        </div>
      )}

      {/* Top metadata */}
      <div className="absolute inset-x-0 top-0 z-10 flex items-start justify-between p-4">
        <Badge
          variant="secondary"
          className="border border-slate-600/70 bg-black/50 text-slate-100 backdrop-blur-sm hover:bg-black/50"
        >
          {bot.category}
        </Badge>
        <CountryFlag language={bot.language} accent={bot.language_accent} className="h-5 w-7 drop-shadow-md" />
      </div>

      {/* Bottom gradient + text + CTA */}
      <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/85 via-black/60 to-transparent p-5 pt-20">
        <h3 className="text-xl font-bold leading-tight text-white drop-shadow-md">
          {displayName}
        </h3>
        {bot.entity_name && (
          <p className="mt-0.5 text-xs font-medium text-white/90 drop-shadow">{bot.entity_name}</p>
        )}
        <p className={`text-sm font-medium text-cyan-300 drop-shadow ${bot.entity_name ? 'mt-0.5' : 'mt-0.5'}`}>
          {bot.role || bot.category}
        </p>
        <Button
          type="button"
          className="mt-4 w-full border-0 bg-cyan-600 text-white hover:bg-cyan-500"
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
        >
          <Phone className="mr-2 h-4 w-4" /> Call {displayName}
        </Button>
      </div>
    </div>
  );
};


interface CallWidgetProps {
  bot: VoiceBot;
  voiceApiUrl: string;
  precall: PrecallOptions;
}

const smoothLevel = (current: number, target: number) => {
  // The transport occasionally emits NaN/undefined audio levels before the
  // stream is warm; sanitize both sides so a NaN can't propagate into the orb
  // animation (framer-motion warns "Invalid keyframe value … opacity: NaN").
  const cur = Number.isFinite(current) ? current : 0;
  const tgt = Number.isFinite(target) ? target : 0;
  // attack quickly, decay slowly
  if (tgt > cur) return cur + (tgt - cur) * 0.35;
  return cur * 0.88 + tgt * 0.12;
};

type Transcript = { speaker: 'user' | 'bot'; text: string };

// Bot/user text streams in as many small fragments ("Guten", "Tag,", "hier"…).
// Rather than render one bubble per fragment, merge a fragment into the previous
// bubble when it's the same speaker, starting a new bubble only on speaker change.
const appendFragment = (
  prev: Transcript[],
  speaker: 'user' | 'bot',
  fragment: string,
): Transcript[] => {
  const text = fragment.trim();
  if (!text) return prev;
  const last = prev[prev.length - 1];
  if (last && last.speaker === speaker) {
    // Join with a space, but not before closing punctuation (",", ".", "!", "?", …).
    const sep = /^[.,!?;:)\]}…»"']/.test(text) || last.text.endsWith(' ') ? '' : ' ';
    const merged: Transcript = { speaker, text: `${last.text}${sep}${text}` };
    return [...prev.slice(0, -1), merged];
  }
  return [...prev, { speaker, text }];
};

const formatDuration = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

interface AudioOrbProps {
  level: number;
  label: string;
  sublabel?: string;
  icon: React.ReactNode;
  imageUrl?: string;
  color: 'cyan' | 'violet' | 'slate';
  isSpeaking: boolean;
}

const AudioOrb = ({ level, label, sublabel, icon, imageUrl, color, isSpeaking }: AudioOrbProps) => {
  const [imgError, setImgError] = useState(false);
  const colorMap = {
    cyan: {
      bg: 'bg-cyan-500',
      ring: 'ring-cyan-400/40',
      shadow: 'shadow-cyan-500/30',
      text: 'text-cyan-700 dark:text-cyan-100',
      muted: 'text-cyan-500/80 dark:text-cyan-400/60',
    },
    violet: {
      bg: 'bg-violet-500',
      ring: 'ring-violet-400/40',
      shadow: 'shadow-violet-500/30',
      text: 'text-violet-700 dark:text-violet-100',
      muted: 'text-violet-500/80 dark:text-violet-400/60',
    },
    slate: {
      bg: 'bg-slate-500',
      ring: 'ring-slate-400/40',
      shadow: 'shadow-slate-500/30',
      text: 'text-slate-700 dark:text-slate-100',
      muted: 'text-slate-500/80 dark:text-slate-400/60',
    },
  };
  const c = colorMap[color];
  // Final clamp so the animation inputs are always finite and in [0,1] — guards
  // against any NaN/out-of-range level slipping through to framer-motion.
  const lvl = Number.isFinite(level) ? Math.min(Math.max(level, 0), 1) : 0;

  // Hard caps so the orb + rings never blow up to fill the whole popup, especially
  // on small screens or when the audio level pegs at 1.0. Keep the pulse subtle:
  // the orb is decorative feedback, not the main content.
  const MAX_MAIN_SCALE = 1.05;
  const MAX_RING1_SCALE = 1.08;
  const MAX_RING2_SCALE = 1.12;
  const MAX_SHADOW_BLUR = 16; // px
  const MAX_SHADOW_SPREAD = 0.14; // rem

  // Ease the level so loud/continuous audio doesn't pin the orb at its max size.
  const easedLvl = Math.sqrt(lvl);
  const mainScale = 1 + easedLvl * (MAX_MAIN_SCALE - 1);
  const ring1Scale = 1 + easedLvl * (MAX_RING1_SCALE - 1);
  const ring2Scale = 1 + easedLvl * (MAX_RING2_SCALE - 1);
  const shadowBlur = 8 + easedLvl * (MAX_SHADOW_BLUR - 8);
  const shadowSpread = 0.06 + easedLvl * (MAX_SHADOW_SPREAD - 0.06);

  return (
    <div className="flex flex-col items-center gap-3">
      {/* Hard outer boundary: even if the inner motion orb scales or its glow
          runs away, this fixed-size wrapper clips it to the intended 96/112 px
          circle. The picture can never outgrow this box. */}
      <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 overflow-hidden rounded-full">
        <motion.div
          className={`relative h-full w-full rounded-full overflow-hidden flex items-center justify-center ${c.bg} ring-4 ${c.ring} shadow-md ${c.shadow}`}
          animate={{
            scale: mainScale,
            boxShadow: isSpeaking
              ? `0 0 ${shadowBlur}px ${shadowSpread.toFixed(3)}rem currentColor`
              : `0 0 8px 0.06rem currentColor`,
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        >
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={label}
              className="absolute inset-0 h-full w-full object-cover"
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="relative z-10 text-white/90">{icon}</div>
          )}
          <motion.div
            className="absolute inset-0 z-20 rounded-full border-2 border-white/20"
            animate={{ scale: ring1Scale, opacity: 0.4 - lvl * 0.2 }}
            transition={{ duration: 0.1 }}
          />
          <motion.div
            className="absolute inset-0 z-20 rounded-full border border-white/10"
            animate={{ scale: ring2Scale, opacity: 0.3 - lvl * 0.15 }}
            transition={{ duration: 0.15 }}
          />
        </motion.div>
      </div>
      <div className="text-center">
        <p className={`text-sm font-semibold ${c.text}`}>{label}</p>
        {sublabel && <p className={`text-xs ${c.muted}`}>{sublabel}</p>}
      </div>
    </div>
  );
};

const CallWidget = ({ bot, voiceApiUrl, precall }: CallWidgetProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const clientRef = useRef<PipecatClient | null>(null);
  const [callState, setCallState] = useState<'idle' | 'connecting' | 'connected' | 'error'>('idle');
  const callStateRef = useRef(callState);

  useEffect(() => {
    callStateRef.current = callState;
  }, [callState]);

  // Try to cache the logged-in user's Google profile photo as a data URL so we
  // don't rely on Google's hotlink URL (which can 429 under heavy reloads).
  useEffect(() => {
    cacheUserPhoto();
  }, []);

  // Real ring-back tone played while connecting. We generate a classic
  // "duud-duud" two-beep pattern in the browser so it loops cleanly during
  // Cloud Run cold-start (min-instances=0) without depending on a WAV asset.
  // Playback is stopped as soon as the call connects, errors or hangs up.
  const dialToneTimerRef = useRef<number | null>(null);
  const dialToneCtxRef = useRef<AudioContext | null>(null);
  const stopDialTone = useCallback(() => {
    if (dialToneTimerRef.current) {
      window.clearTimeout(dialToneTimerRef.current);
      dialToneTimerRef.current = null;
    }
    const ctx = dialToneCtxRef.current;
    if (ctx && ctx.state !== 'closed') {
      ctx.close().catch(() => {});
    }
    dialToneCtxRef.current = null;
  }, []);
  const startDialTone = useCallback(() => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      stopDialTone();
      const ctx = new AudioCtx();
      dialToneCtxRef.current = ctx;
      const playBeep = (offset: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(440, ctx.currentTime + offset);
        gain.gain.setValueAtTime(0, ctx.currentTime + offset);
        gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + offset + 0.03);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + offset + 0.18);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + offset);
        osc.stop(ctx.currentTime + offset + 0.18);
      };
      const schedule = () => {
        if (!dialToneCtxRef.current) return;
        playBeep(0);
        playBeep(0.28);
        dialToneTimerRef.current = window.setTimeout(schedule, 1200);
      };
      schedule();
    } catch {
      // ignore audio-generation failures
    }
  }, [stopDialTone]);

  useEffect(() => {
    // Stop the ring-back tone as soon as we leave the connecting state.
    // We deliberately do NOT stop it on every effect cleanup, because the
    // cleanup runs immediately after the state changes to 'connecting' and
    // would silence the tone before the user can hear it.
    if (callState !== 'connecting') {
      stopDialTone();
    }
  }, [callState, stopDialTone]);

  const [statusText, setStatusText] = useState<string>('Ready to call');
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [userLevel, setUserLevel] = useState(0);
  const [botLevel, setBotLevel] = useState(0);
  const [userSpeaking, setUserSpeaking] = useState(false);
  const [botSpeaking, setBotSpeaking] = useState(false);

  // Pipecat audio levels can arrive as raw dB (negative values) for the local
  // mic and as a 0-1 float for remote audio. Normalize both to a 0-1 range so
  // the user orb animates just like the bot orb.
  const normalizeAudioLevel = (level: number): number => {
    if (!Number.isFinite(level)) return 0;
    if (level >= 0 && level <= 1) return level;
    if (level > 1) return Math.min(level, 1);
    // dB value: map [-60, 0] -> [0, 1]
    const db = Math.max(-60, Math.min(0, level));
    return 1 - Math.abs(db) / 60;
  };
  const [duration, setDuration] = useState(0);
  const userLevelRef = useRef(0);
  const botLevelRef = useRef(0);
  const userHistoryRef = useRef<{ t: number; v: number }[]>([]);
  const botHistoryRef = useRef<{ t: number; v: number }[]>([]);
  const userSpeakingRef = useRef(false);
  const botSpeakingRef = useRef(false);
  const rafRef = useRef<number | undefined>(undefined);
  const connectedAtRef = useRef<number | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement>(null);

  // Local microphone analyser — provides reliable user-orb feedback even when
  // the Pipecat transport does not emit local audio levels.
  const localAudioCtxRef = useRef<AudioContext | null>(null);
  const localAnalyserRef = useRef<AnalyserNode | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const localDataArrayRef = useRef<Uint8Array | null>(null);

  const startLocalAudioMonitor = useCallback(async () => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;
      const ctx = new AudioCtx();
      localAudioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.4;
      source.connect(analyser);
      localAnalyserRef.current = analyser;
      localDataArrayRef.current = new Uint8Array(analyser.fftSize);
      await ctx.resume();
    } catch (err) {
      console.warn('[telephony] local audio monitor failed:', err);
    }
  }, []);

  const stopLocalAudioMonitor = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    localAnalyserRef.current = null;
    localDataArrayRef.current = null;
    if (localAudioCtxRef.current && localAudioCtxRef.current.state !== 'closed') {
      localAudioCtxRef.current.close().catch(() => {});
    }
    localAudioCtxRef.current = null;
  }, []);

  const buildWsUrl = useCallback(() => {
    const protocol = voiceApiUrl.startsWith('https:') ? 'wss:' : 'ws:';
    const host = new URL(voiceApiUrl).host;
    // Opening language: user override (already a 2-letter code) wins, else the
    // bot's own default. The kernel still auto-adapts to the caller mid-call.
    const p2_language = precall.language || botLanguageCode(bot.language);
    // Accent: explicit user override wins. If none is set and the user kept the
    // agent's default language, fall back to the bot's configured accent.
    const p2_language_accent =
      (precall.language_accent || '').trim() ||
      (!precall.language && bot.language_accent ? bot.language_accent : '');
    const params = new URLSearchParams({
      bot_key: bot.key,
      p2_language,
      voice_backend: bot.voice_backend,
      direction: 'inbound',
      caller_mode: 'full_ai',
    });
    if (p2_language_accent) params.set('p2_language_accent', p2_language_accent);
    // p2_caller_name = the BOT's OWN name (the voice the caller hears). Only send
    // if the user named the bot; empty → the brand/kernel's own name governs.
    if ((precall.botName || '').trim()) params.set('p2_caller_name', (precall.botName || '').trim());
    if (precall.voice) params.set('p2_gemini_voice', precall.voice);
    return `${protocol}//${host}/ws/pipecat?${params.toString()}`;
  }, [bot, voiceApiUrl, precall]);

  // Build a FRESH PipecatClient per call. The @pipecat-ai/websocket-transport
  // player creates its AudioContext + worklet on connect() and tears them down
  // on disconnect(); reusing the same client for a second call leaves a dead
  // player → silent bot on the 2nd+ call. So we construct a new client each
  // startCall and dispose it on hangup/disconnect.
  const createClient = useCallback(() => {
    return new PipecatClient({
      transport: new WebSocketTransport({
        // Decoupled: recorder 16 kHz (Silero VAD on the server only accepts
        // 8k/16k) / player 24 kHz (Gemini native, played 1:1). See the rate
        // constants above. Matches the pipecat freeze-test reference config.
        recorderSampleRate: RECORDER_SAMPLE_RATE,
        playerSampleRate: PLAYER_SAMPLE_RATE,
      }),
      enableMic: true,
      enableCam: false,
      callbacks: {
        onTransportStateChanged: (state: TransportState) => {
          setStatusText(`Transport: ${state}`);
        },
        onConnected: () => {
          setCallState('connected');
          setStatusText('Connected — bot will greet you');
          connectedAtRef.current = Date.now();
          // Safari guard: the player worklet plays bot PCM 1:1 and assumes the
          // output AudioContext honored our requested 24 kHz. Some browsers
          // (historically iOS Safari) silently pin the context to the hardware
          // rate (e.g. 48 kHz) instead, which would play the bot too fast.
          // The library's player has no self-correction (the recorder does), so
          // we at least detect and surface the mismatch instead of shipping
          // silent chipmunk audio. Reaches into library internals defensively.
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ctx = (clientRef.current?.transport as any)?._mediaManager
              ?._wavStreamPlayer?.context as AudioContext | undefined;
            if (ctx && Math.abs(ctx.sampleRate - PLAYER_SAMPLE_RATE) > 1) {
              console.warn(
                `[telephony] Player AudioContext sampleRate=${ctx.sampleRate} ` +
                  `but expected ${PLAYER_SAMPLE_RATE}. Bot audio may play at the ` +
                  `wrong speed on this browser (known iOS Safari quirk).`,
              );
            }
          } catch {
            // Internals not reachable on this library version — skip the check.
          }
        },
        onDisconnected: () => {
          // Drop the ref so a fresh client is built next call (the torn-down
          // transport here can't be reused for audio).
          clientRef.current = null;
          if (callStateRef.current === 'error') return;
          setCallState('idle');
          setStatusText('Call ended');
          connectedAtRef.current = null;
          setDuration(0);
        },
        onBotReady: () => {
          setStatusText('Bot ready — listening');
        },
        onUserTranscript: (transcript: TranscriptData) => {
          if (transcript.final && transcript.text) {
            setTranscripts((prev) => appendFragment(prev, 'user', transcript.text));
          }
        },
        onBotOutput: (data: BotOutputData) => {
          if (data.aggregated_by === 'sentence' && data.text) {
            setTranscripts((prev) => appendFragment(prev, 'bot', data.text));
          }
        },
        onTrackStarted: (track: MediaStreamTrack, participant?: Participant) => {
          if (!participant?.local && track.kind === 'audio' && audioRef.current) {
            const el = audioRef.current;
            el.volume = 1;
            el.muted = false;
            el.srcObject = new MediaStream([track]);
            el.play().catch(() => {
              // Browser autoplay policies may block playback until a user gesture.
            });
          }
        },
        onLocalAudioLevel: (level: number) => {
          userLevelRef.current = smoothLevel(userLevelRef.current, normalizeAudioLevel(level));
        },
        onRemoteAudioLevel: (level: number) => {
          botLevelRef.current = smoothLevel(botLevelRef.current, normalizeAudioLevel(level));
        },
        onServerMessage: (msg: unknown) => {
          if (typeof msg !== 'string') return;
          try {
            const parsed = JSON.parse(msg);
            if (parsed.type === 'error') {
              const errorText = parsed.content || parsed.message || 'Unknown server error';
              setCallState('error');
              setStatusText(`Server error: ${errorText}`);
            }
          } catch {
            // ignore non-JSON text messages
          }
        },
        onError: (message) => {
          const errorText =
            typeof message?.data === 'string'
              ? message.data
              : JSON.stringify(message?.data ?? 'Unknown transport error');
          console.error('Pipecat transport error:', message);
          setCallState('error');
          setStatusText(`Connection error: ${errorText}`);
        },
      },
    } as PipecatClientOptions);
  }, [buildWsUrl]);

  // Dispose the active client (disconnect + drop the ref) so the next call
  // starts from a clean transport/AudioContext.
  const disposeClient = useCallback(() => {
    const client = clientRef.current;
    clientRef.current = null;
    client?.disconnect().catch(() => {});
  }, []);

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      stopDialTone();
      clientRef.current?.disconnect().catch(() => {});
      clientRef.current = null;
    };
  }, []);

  // Mobile keep-alive: iOS/Android suspend AudioContexts when the screen locks,
  // the tab backgrounds, or another call/Siri interrupts. A suspended context
  // produces silence (mic dead + bot inaudible) with no error. When the page
  // becomes visible again we resume both the player and recorder contexts.
  // Mirrors the pattern ghostly_raisy uses for the same OS behavior.
  useEffect(() => {
    if (callState !== 'connected') return;
    const resumeContexts = () => {
      if (document.visibilityState !== 'visible') return;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const mm = (clientRef.current?.transport as any)?._mediaManager;
        const ctxs: Array<AudioContext | undefined> = [
          mm?._wavStreamPlayer?.context,
          mm?._wavRecorder?.context,
          mm?._mediaStreamRecorder?.context,
        ];
        for (const ctx of ctxs) {
          if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
        }
      } catch {
        // Internals not reachable on this library version — nothing to resume.
      }
    };
    document.addEventListener('visibilitychange', resumeContexts);
    return () => document.removeEventListener('visibilitychange', resumeContexts);
  }, [callState]);

  // Animation loop: decay levels smoothly and update React state at ~60fps.
  // Speaking detection uses a rolling 2-second average so it adapts to the
  // user's own noise floor instead of a hard-coded threshold.
  useEffect(() => {
    const computeSpeaking = (current: number, history: { t: number; v: number }[], now: number) => {
      history.push({ t: now, v: current });
      const cutoff = now - 2000;
      while (history.length > 0 && history[0].t <= cutoff) {
        history.shift();
      }
      const avg = history.reduce((sum, s) => sum + s.v, 0) / (history.length || 1);
      // Current level must be clearly above the recent average (adaptive floor)
      // with a small absolute minimum so silence doesn't trigger speech.
      const threshold = Math.max(0.03, avg * 1.5 + 0.015);
      return current > threshold;
    };

    const loop = () => {
      // Read the local microphone analyser for reliable user-orb feedback.
      const analyser = localAnalyserRef.current;
      const dataArray = localDataArrayRef.current;
      if (analyser && dataArray) {
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const v = (dataArray[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / dataArray.length);
        // Boost low speech levels so the orb visibly reacts to normal talking.
        const boosted = Math.min(1, rms * 8);
        userLevelRef.current = smoothLevel(userLevelRef.current, boosted);
      } else {
        userLevelRef.current *= 0.92;
      }
      botLevelRef.current *= 0.92;
      setUserLevel(userLevelRef.current);
      setBotLevel(botLevelRef.current);

      const now = performance.now();
      const nextUserSpeaking = computeSpeaking(userLevelRef.current, userHistoryRef.current, now);
      if (nextUserSpeaking !== userSpeakingRef.current) {
        userSpeakingRef.current = nextUserSpeaking;
        setUserSpeaking(nextUserSpeaking);
      }
      const nextBotSpeaking = computeSpeaking(botLevelRef.current, botHistoryRef.current, now);
      if (nextBotSpeaking !== botSpeakingRef.current) {
        botSpeakingRef.current = nextBotSpeaking;
        setBotSpeaking(nextBotSpeaking);
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Auto-scroll the transcript to the newest line as it streams in, so the
  // viewer can follow along without having to drag the scrollbar themselves.
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [transcripts]);

  // Call duration timer
  useEffect(() => {
    if (callState !== 'connected') return;
    const interval = setInterval(() => {
      if (connectedAtRef.current) {
        setDuration(Math.floor((Date.now() - connectedAtRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [callState]);

  // Start/stop the local mic level monitor so the user orb gives live feedback.
  useEffect(() => {
    if (callState === 'connected') {
      startLocalAudioMonitor();
    } else {
      stopLocalAudioMonitor();
    }
    return () => stopLocalAudioMonitor();
  }, [callState, startLocalAudioMonitor, stopLocalAudioMonitor]);

  const startCall = async () => {
    try {
      setCallState('connecting');
      setStatusText('Connecting…');
      setTranscripts([]);
      setDuration(0);
      // Always start from a fresh client so a previous call's torn-down audio
      // player can't leave the 2nd call silent.
      disposeClient();
      // Play the generated "duud-duud" ring-back tone from the user gesture.
      // It is stopped on connect/error and will loop cleanly during the extra
      // Cloud Run cold-start time now that voice runs at min-instances=0.
      startDialTone();
      const client = createClient();
      clientRef.current = client;
      await client.initDevices();
      await client.connect({ wsUrl: buildWsUrl() });
    } catch (err) {
      console.error('Pipecat connect failed:', err);
      disposeClient();
      setCallState('error');
      setStatusText(`Connection failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const hangUp = () => {
    disposeClient();
    setCallState('idle');
    setStatusText('Call ended');
  };

  const isActive = callState === 'connecting' || callState === 'connected';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-center gap-2 text-green-400">
        <CheckCircle className="w-5 h-5" />
        <span>
          Access granted. You are calling <strong>{bot.persona_name || bot.name}</strong>.
        </span>
      </div>

      <div className="bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-700 rounded-3xl p-4 sm:p-8 backdrop-blur-sm">
        {/* Header: status + duration */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <div
              className={`w-2.5 h-2.5 rounded-full ${
                callState === 'connected' ? 'bg-green-400 animate-pulse' : 'bg-slate-400 dark:bg-slate-500'
              }`}
            />
            <span className="text-sm text-slate-600 dark:text-slate-300">{statusText}</span>
          </div>
          <span className="text-sm font-mono text-slate-500 dark:text-slate-400">{formatDuration(duration)}</span>
        </div>

        {/* Ringing indicator while the voice service cold-starts (min=0) */}
        {callState === 'connecting' && (
          <div className="text-center mb-4">
            <motion.span
              className="inline-flex items-center gap-2 text-cyan-400 font-medium"
              animate={{ opacity: [1, 0.5, 1] }}
              transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Phone className="w-4 h-4" />
              duud-duud…
            </motion.span>
          </div>
        )}

        {/* Dual orb stage */}
        <div className="relative flex items-center justify-center gap-6 sm:gap-12 mb-8">
          {/* Connection beam */}
          {callState === 'connected' && (
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 sm:w-40 h-px bg-gradient-to-r from-cyan-500/60 via-violet-500/60 to-cyan-500/60"
              animate={{ opacity: [0.3, 0.8, 0.3] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            />
          )}

          <AudioOrb
            level={userLevel}
            label="You"
            sublabel={userSpeaking ? 'speaking' : 'listening'}
            icon={<User className="w-10 h-10" />}
            imageUrl={getUserPhotoUrl() || undefined}
            color="cyan"
            isSpeaking={userSpeaking}
          />

          <AudioOrb
            level={botLevel}
            label={bot.persona_name || bot.name}
            sublabel={botSpeaking ? 'speaking' : 'listening'}
            icon={<Bot className="w-10 h-10" />}
            imageUrl={getBotImageUrl(bot.key, 256)}
            color="violet"
            isSpeaking={botSpeaking}
          />
        </div>

        {/* Controls */}
        <div className="flex justify-center mb-6">
          <Button
            type="button"
            size="lg"
            onClick={isActive ? hangUp : startCall}
            className={
              isActive
                ? 'bg-red-600 hover:bg-red-700 text-white px-8 rounded-full'
                : 'bg-cyan-600 hover:bg-cyan-700 text-white px-8 rounded-full'
            }
          >
            {callState === 'connecting' ? (
              'Connecting…'
            ) : isActive ? (
              <>
                <PhoneOff className="w-4 h-4 mr-2" /> Hang Up
              </>
            ) : (
              <>
                <Phone className="w-4 h-4 mr-2" /> Call {bot.persona_name || bot.name}
              </>
            )}
          </Button>
        </div>

        {callState === 'error' && (
          <div className="text-red-400 text-sm bg-red-950/30 border border-red-900 rounded-lg p-3 mb-6 text-center">
            {statusText}
          </div>
        )}

        {/* Transcripts */}
        {transcripts.length > 0 && (
          <div className="max-h-48 overflow-y-auto space-y-3 pr-1">
            {transcripts.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${t.speaker === 'bot' ? 'justify-start' : 'justify-end'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                    t.speaker === 'bot'
                      ? 'bg-violet-100 text-violet-900 border border-violet-200 dark:bg-violet-950/50 dark:text-violet-100 dark:border-violet-800/50 rounded-tl-sm'
                      : 'bg-cyan-100 text-cyan-900 border border-cyan-200 dark:bg-cyan-950/50 dark:text-cyan-100 dark:border-cyan-800/50 rounded-tr-sm'
                  }`}
                >
                  <span className="font-semibold text-xs uppercase opacity-70">{t.speaker}</span>
                  <p className="mt-0.5">{t.text}</p>
                </div>
              </motion.div>
            ))}
            <div ref={transcriptEndRef} />
          </div>
        )}

        <audio ref={audioRef} autoPlay playsInline className="hidden" />
      </div>

      <p className="text-xs text-slate-400 text-center">
        Uses the Pipecat client SDK. Requires microphone access.
      </p>
    </div>
  );
};


interface BotSelectorProps {
  bots: VoiceBot[];
  loading: boolean;
  selectedBotKey: string;
  onSelect: (key: string) => void;
}

const BotSelector = ({ bots, loading, onSelect }: BotSelectorProps) => {
  const categories = useMemo(
    () => ['All', ...Array.from(new Set(bots.map((b) => b.category)))],
    [bots],
  );
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredBots = useMemo(() => {
    let list = activeCategory === 'All' ? bots : bots.filter((b) => b.category === activeCategory);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((b) =>
        (b.key || '').toLowerCase().includes(q) ||
        (b.name || '').toLowerCase().includes(q) ||
        (b.persona_name || '').toLowerCase().includes(q) ||
        (b.role || '').toLowerCase().includes(q) ||
        (b.entity_name || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [bots, activeCategory, searchQuery]);

  if (loading) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">Loading AI agents…</div>
    );
  }

  if (bots.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500 dark:text-slate-400">
        No public demo agents configured yet.
      </div>
    );
  }

  return (
    <div className="space-y-6 mb-12">
      {/* Search + category filters */}
      <div className="max-w-xl mx-auto space-y-4">
        <div className="relative">
          <Input
            type="text"
            placeholder="Search bots, names, roles or companies (e.g. ROWI, Pop Meals, Airbnb)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white pr-4 pl-4 h-11 rounded-full"
          />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3">
          {categories.map((category) => {
            const active = activeCategory === category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                  active
                    ? 'bg-cyan-600 text-white border-cyan-500 shadow-lg shadow-cyan-950/40'
                    : 'bg-white dark:bg-slate-900/50 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      {filteredBots.length === 0 ? (
        <div className="text-center py-8 text-slate-500 dark:text-slate-400">No agents match your search.</div>
      ) : (
        // Full grid — every agent is visible at once (no carousel pages), so no
        // bot goes undiscovered. Clicking one opens the call popup.
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 max-w-6xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {filteredBots.map((bot) => (
            <motion.div key={bot.key} variants={itemVariants}>
              <BotDemoCard bot={bot} onSelect={() => onSelect(bot.key)} />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
};
